<template>
  <WdsSelect
    v-model="selectedEndClients"
    v-model:search="search"
    multi-select
    searchable
    custom-search
    persist-input-on-select
    :label="meta.filter.quickFilter ? '' : $t('End clients')"
    :list="endClientOptions"
    :placeholder="$t('Select end clients to filter by')"
    max-labels-visible="5"
    :load-more-distance="10"
    can-deselect
    labels-primary
    @load-more="loadMore"
  />
</template>

<script setup lang="ts">
import { computed, ModelRef, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsSelect } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

type EndClientOption = { label: string; value: string }

const selectedEndClients: ModelRef<EndClientOption[]> = defineModel({
  default: () => [],
})

defineProps<{ meta: FilterMetaDataProp }>()

const SUPPLIER_CLIENT_OPTIONS = graphql(`
  query EndClientsFilterOptions($accounts: [ID!], $search: String, $page: Int) {
    companyClients(accounts: $accounts, search: $search, first: 25, page: $page) {
      data {
        id
        company {
          id
          name
        }
      }
      paginatorInfo {
        currentPage
        hasMorePages
      }
    }
  }
`)

const AGENCY_CLIENT_OPTIONS = graphql(`
  query AgencyEndClientsFilterOptions($accounts: [ID!], $search: String, $page: Int) {
    staffingAgencyClients(accounts: $accounts, search: $search, first: 25, page: $page) {
      data {
        id
        client {
          id
          name
        }
      }
      paginatorInfo {
        currentPage
        hasMorePages
      }
    }
  }
`)

const { selectedAccounts } = useAccountsStore()
const { isRecruiter } = useAccountCapabilities()

const search = ref('')

const accountsVariables = () => ({
  search: search.value,
  page: 1,
  ...(selectedAccounts.length === 0
    ? {}
    : { accounts: selectedAccounts.map((account) => account.id) }),
})

const {
  result: supplierResult,
  loading: supplierLoading,
  fetchMore: fetchMoreSupplier,
} = useQuery(SUPPLIER_CLIENT_OPTIONS, accountsVariables, () => ({
  clientId: 'publicApi',
  enabled: !isRecruiter.value,
}))

// The accounts store never holds staffing agency accounts, so agencies fall back to the table's own scope.
const agencyAccountsVariables = () =>
  selectedAccounts.length === 0
    ? { ...accountsVariables(), accounts: [currentAccount.globalId] }
    : accountsVariables()

const {
  result: agencyResult,
  loading: agencyLoading,
  fetchMore: fetchMoreAgency,
} = useQuery(AGENCY_CLIENT_OPTIONS, agencyAccountsVariables, () => ({
  clientId: 'publicApi',
  enabled: isRecruiter.value,
}))

// Pages are appended here rather than by a cache policy, which would also merge the clients tables' own pages.
function loadMore() {
  if (isRecruiter.value) {
    const page = agencyResult.value?.staffingAgencyClients.paginatorInfo
    if (agencyLoading.value || !page?.hasMorePages) return

    fetchMoreAgency({
      variables: { page: page.currentPage + 1 },
      updateQuery: (previous, { fetchMoreResult }) => {
        const next = fetchMoreResult?.staffingAgencyClients
        if (!next) return previous

        return {
          staffingAgencyClients: { ...next, data: [...previous.staffingAgencyClients.data, ...next.data] },
        }
      },
    })
    return
  }

  const page = supplierResult.value?.companyClients.paginatorInfo
  if (supplierLoading.value || !page?.hasMorePages) return

  fetchMoreSupplier({
    variables: { page: page.currentPage + 1 },
    updateQuery: (previous, { fetchMoreResult }) => {
      const next = fetchMoreResult?.companyClients
      if (!next) return previous

      return { companyClients: { ...next, data: [...previous.companyClients.data, ...next.data] } }
    },
  })
}

// An agency can reach one client through several links, so its options are deduplicated.
const endClientOptions = computed<EndClientOption[]>(() => {
  const clients = isRecruiter.value
    ? (agencyResult.value?.staffingAgencyClients?.data.map((row) => row.client) ?? [])
    : (supplierResult.value?.companyClients?.data.map((row) => row.company) ?? [])

  return [...new Map(clients.map((client) => [client.id, client])).values()].map((client) => ({
    label: client.name,
    value: client.id,
  }))
})
</script>
